import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
	ApiError,
	createDraft,
	deleteDraft,
	fetchDraft,
	fetchDraftPool,
	updateDraft,
} from '@/lib/api.js';
import BudgetBar from './draft/BudgetBar.jsx';
import DraftPitch from './draft/DraftPitch.jsx';
import PlayerList from './draft/PlayerList.jsx';
import PublishedBar from './draft/PublishedBar.jsx';
import { useDraftSquad } from './draft/useDraftSquad.js';
import { randomDraftName } from './draft/draftNames.js';
import { autoPickSquad } from './draft/autoPick.js';
import { MAX_NAME_LENGTH, firstFreeSlot } from './draft/draftRules.js';
import { timeAgo } from '@/lib/format.js';
import './DraftPage.css';

/*
 * Endpoints report some failures with a status and a body, and some
 * in-band; the useful sentence is in the body either way.
 */
function apiMessage(error, fallback) {
	if (error?.body?.message) return error.body.message;
	if (error instanceof ApiError && error.status !== 404) return error.message;
	return fallback;
}

/* The pool is 577 players and does not change within a session. Cached so
 * that publishing — which swaps /draft for /draft/<slug> and remounts —
 * costs one small draft fetch rather than the whole list again. */
let cachedPool = null;

function Builder({ slug, draft, pool, fork, justPublished }) {
	const navigate = useNavigate();
	const source = draft || fork;
	const readOnly = Boolean(draft && !draft.can_edit);

	/* A new draft opens already named, so publishing without typing
	 * anything still produces something better than "Untitled draft".
	 * Computed once, in the initialiser, or every render would reroll it. */
	const builder = useDraftSquad({
		name: fork ? `${source.name} (copy)` : source?.name || randomDraftName(),
		draft: source,
	});

	/* A player lifted off the pitch for a substitution. */
	const [selection, setSelection] = useState(null);
	const [saveState, setSaveState] = useState({ status: 'idle', message: null });
	/* Tracks only the dismissal, so the bar still appears if the flag
	 * arrives after this component has mounted. */
	const [publishDismissed, setPublishDismissed] = useState(false);

	useEffect(() => {
		if (!selection) return undefined;
		const onKeyDown = (event) => {
			if (event.key === 'Escape') setSelection(null);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [selection]);

	const onSlotSelect = useCallback(
		(slot) => {
			if (readOnly) return;
			const player = builder.squad[slot.position][slot.index];
			if (!player) return;

			if (selection?.position === slot.position && selection?.index === slot.index) {
				setSelection(null);
				return;
			}

			if (selection && builder.canSwap(selection, slot)) {
				/* Two substitutes: only their order changes, so this is a
				 * reorder rather than a swap of squad slots. */
				if (selection.benchIndex != null && slot.benchIndex != null) {
					builder.reorderBench(selection.benchIndex, slot.benchIndex);
				} else {
					builder.substitute(selection, slot);
				}
				setSelection(null);
				return;
			}

			setSelection(slot);
		},
		[builder, readOnly, selection],
	);

	const onSlotRemove = useCallback(
		(slot) => {
			setSelection(null);
			builder.remove(slot.position, slot.index);
		},
		[builder],
	);

	/* A player goes to the first free slot of their own position — the
	 * quotas are fixed, so there is only ever one place they can land. */
	const onPick = useCallback(
		(player) => {
			const slot = firstFreeSlot(builder.squad, player.position);
			if (!slot) return;
			builder.place(slot.position, slot.index, player);
		},
		[builder],
	);

	const publish = async () => {
		setSaveState({ status: 'saving', message: null });
		try {
			const result = await createDraft(builder.payload());
			builder.markSaved();
			setSaveState({ status: 'idle', message: null });
			/* Replace rather than push: the empty /draft this came from is
			 * not somewhere Back should return to. */
			navigate(`/draft/${result.slug}`, { replace: true, state: { justPublished: true } });
		} catch (error) {
			setSaveState({
				status: 'error',
				message: apiMessage(error, 'Could not publish this draft.'),
			});
		}
	};

	const save = async () => {
		setSaveState({ status: 'saving', message: null });
		try {
			await updateDraft(slug, builder.payload());
			builder.markSaved();
			setSaveState({ status: 'saved', message: 'Saved.' });
		} catch (error) {
			setSaveState({
				status: 'error',
				message: apiMessage(error, 'Could not save these changes.'),
			});
		}
	};

	const destroy = async () => {
		if (!window.confirm('Delete this draft? The link will stop working for everyone.')) {
			return;
		}
		try {
			await deleteDraft(slug);
			navigate('/draft', { replace: true });
		} catch (error) {
			setSaveState({
				status: 'error',
				message: apiMessage(error, 'Could not delete this draft.'),
			});
		}
	};

	/* Still gates the button, but is not printed: an incomplete squad is
	 * obvious from the empty slots on the pitch, and saying "15 to pick"
	 * next to a greyed-out Publish only restates them. */
	const canPublish = builder.complete && builder.problems.length === 0;

	return (
		<div className="draft-page">
			{justPublished && slug && !publishDismissed && (
				<PublishedBar slug={slug} onDismiss={() => setPublishDismissed(true)} />
			)}

			<header className="draft-page__bar">
				{readOnly ? (
					<h1 className="draft-page__title">{draft.name}</h1>
				) : (
					<div className="draft-page__naming">
						<input
							className="draft-page__name"
							type="text"
							value={builder.name}
							maxLength={MAX_NAME_LENGTH}
							placeholder="Untitled draft"
							aria-label="Draft name"
							onChange={(event) => builder.setName(event.target.value)}
						/>
						<button
							type="button"
							className="draft-page__reroll"
							title="Suggest another name"
							aria-label="Suggest another name"
							onClick={() => builder.setName(randomDraftName())}
						>
							⟳
						</button>
					</div>
				)}
				<BudgetBar spent={builder.spent} remaining={builder.remaining} />
			</header>


			<div className="draft-page__layout">
				<div className="draft-page__pitch">
					<DraftPitch
						squad={builder.squad}
						shape={builder.shape}
						bench={builder.bench}
						fixtures={pool.fixtures}
						editable={!readOnly}
						selection={selection}
						canSwap={builder.canSwap}
						onSlotSelect={onSlotSelect}
						onSlotRemove={onSlotRemove}
					/>

					<div className="draft-page__actions">
						{readOnly ? null : (
							<>
								<button
									type="button"
									className="button"
									onClick={slug ? save : publish}
									disabled={!canPublish || saveState.status === 'saving'}
								>
									{saveState.status === 'saving'
										? 'Saving…'
										: slug
											? 'Save'
											: 'Publish'}
								</button>
								{slug && (
									<button
										type="button"
										className="button button--danger"
										onClick={destroy}
									>
										Delete
									</button>
								)}
								<button
									type="button"
									className="button button--secondary"
									onClick={() => {
										const squad = autoPickSquad(pool.players);
										if (squad) {
											builder.replaceSquad(squad);
											setSelection(null);
										}
									}}
								>
									Auto pick
								</button>
								{!slug && builder.filled > 0 && (
									<button
										type="button"
										className="button button--secondary"
										onClick={() => {
											builder.clear();
											setSelection(null);
										}}
									>
										Reset
									</button>
								)}
							</>
						)}
						{saveState.message && (
							<span
								className={`draft-page__note${
									saveState.status === 'error' ? ' draft-page__note--error' : ''
								}`}
								role={saveState.status === 'error' ? 'alert' : 'status'}
							>
								{saveState.message}
							</span>
						)}

						{/* Only for a draft that exists — an unpublished one has no
						    history to report. `updated` moves on every save, so it
						    reads as the last time the squad actually changed, which
						    is why it belongs beside the button that changes it. */}
						{draft?.updated && (
							<span className="draft-page__note">
								Updated{' '}
								<time
									dateTime={draft.updated}
									title={new Date(draft.updated).toLocaleString()}
								>
									{timeAgo(draft.updated)}
								</time>
							</span>
						)}
					</div>
				</div>

				{!readOnly && (
					<PlayerList players={pool.players} squad={builder.squad} onPick={onPick} />
				)}

				{readOnly && (
					<aside className="draft-page__side">
						{/* Most people who reach this page arrived from a link
						    somebody sent them, and the only thing offered to them
						    was a copy of that person's squad. This is the column
						    the player list occupies when the draft is yours, so
						    there is room to ask. */}
						{/* Both ways to end up with a draft, in one place. They were
						    a button under the pitch and a button in this panel,
						    which read as the same offer made twice — what differs
						    is only whether you keep these fifteen. */}
						<div className="draft-page__invite">
							<h2>Fancy your own?</h2>
							<p>
								Fifteen players, no sign-up. Yours gets a link just like this
								one.
							</p>
							<div className="draft-page__invite-actions">
								<Link className="button" to="/draft">
									Build a squad
								</Link>
								<button
									type="button"
									className="button button--secondary"
									onClick={() => navigate('/draft', { state: { fork: draft } })}
								>
									Start from this one
								</button>
							</div>
						</div>
					</aside>
				)}
			</div>
		</div>
	);
}

export default function DraftPage() {
	const { slug } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [pool, setPool] = useState(null);
	const [draft, setDraft] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);

	/* Consume the just-published flag on arrival.
	 *
	 * Held in state rather than read straight from location: browsers
	 * restore history state on reload, so it has to be cleared, but the
	 * clearing effect runs while this page is still fetching — wiping it
	 * before Builder has mounted to see it. Copying it here first keeps the
	 * confirmation for this render and still keeps it off a refresh. */
	const [justPublished, setJustPublished] = useState(false);
	useEffect(() => {
		if (location.state?.justPublished) {
			setJustPublished(true);
			navigate(location.pathname, { replace: true, state: null });
		}
	}, [location.state, location.pathname, navigate]);

	useEffect(() => {
		let cancelled = false;
		setStatus('loading');

		const requests = [cachedPool ? Promise.resolve(cachedPool) : fetchDraftPool()];
		if (slug) requests.push(fetchDraft(slug));

		Promise.all(requests)
			.then(([poolData, draftData]) => {
				if (cancelled) return;
				cachedPool = poolData;
				setPool(poolData);
				setDraft(draftData || null);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(
					err?.status === 404
						? 'That draft does not exist, or it has been deleted.'
						: apiMessage(err, 'Could not load the player list.'),
				);
				setStatus('error');
			});

		return () => {
			cancelled = true;
		};
	}, [slug]);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading players…</span>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not open this page.</strong>
				<span>{error}</span>
				<Link to="/draft">Start a new draft</Link>
			</div>
		);
	}

	/* React Router renders this same element for /draft and /draft/<slug>,
	   so leaving one for the other does not unmount it: the draft fetched
	   for the previous slug is still in state for the render that happens
	   before the loading effect runs. Builder reads its opening squad from
	   this prop once, on mount, so that one stale render was enough to
	   start a blank draft on somebody else's fifteen — "Build a squad" and
	   "Start from this one" did exactly the same thing. */
	const current = draft && draft.slug === slug ? draft : null;

	return (
		<Builder
			key={slug || 'new'}
			slug={slug}
			draft={current}
			pool={pool}
			fork={location.state?.fork}
			justPublished={justPublished}
		/>
	);
}
